import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { DatasetListItem } from "datasets/types/interfaces/datasetListItem";
import { Bot, Minus, PanelRightOpen, SquarePen, UserRoundPlus } from "lucide-react";
import useSWR from "swr";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import useNextGenHeader from "~/contexts/nextgen-header";
import { usePageContext } from "~/contexts/page-context";
import useUser from "~/contexts/user";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { useFetcher } from "~/hooks/useFetcher";
import { useProjectAppsFeature } from "~/hooks/useProjectAppsFeature";
import { useUserPreferences } from "~/hooks/useUserPreferences";
import { cn } from "~/lib/utils";
import { buildEntityBreadcrumbs } from "~/pages/manifold-nextgen/breadcrumbs";
import { ResourceLoadError } from "~/pages/manifold-nextgen/components/ResourceLoadError";
import {
  NEXTGEN_CHAT_EVENT,
  trackNextgenChatEvent,
} from "~/pages/manifold-nextgen/components/conversations/analytics";
import { McpAppsPaneProvider } from "~/pages/manifold-nextgen/components/conversations/useMcpAppsPane";
import { SessionArtifactAppsProvider } from "~/pages/manifold-nextgen/components/conversations/useSessionArtifactApps";
import { SessionArtifactsProvider } from "~/pages/manifold-nextgen/components/conversations/useSessionArtifacts";
import { SessionDagRunsProvider } from "~/pages/manifold-nextgen/components/conversations/useSessionDagRuns";
import { SessionEvalRunsProvider } from "~/pages/manifold-nextgen/components/conversations/useSessionEvalRuns";
import { SessionIngestsProvider } from "~/pages/manifold-nextgen/components/conversations/useSessionIngests";
import { SessionPlanProvider } from "~/pages/manifold-nextgen/components/conversations/useSessionPlan";
import { ProjectShareRecipientWarning } from "~/pages/manifold-nextgen/components/projects/ProjectShareRecipientWarning";
import { OwnersOnlyDialog } from "~/pages/manifold-nextgen/components/share/OwnersOnlyDialog";
import { ShareModal } from "~/pages/manifold-nextgen/components/share/ShareModal";
import { useDatasetsById } from "~/pages/manifold-nextgen/hooks/useDatasetsById";
import { useProjectNeedsSetup } from "~/pages/manifold-nextgen/hooks/useProjectNeedsSetup";
import { useProjectShare } from "~/pages/manifold-nextgen/hooks/useProjectShare";
import {
  SessionContextPanelProvider,
  useSessionContextPanel,
} from "~/pages/manifold-nextgen/hooks/useSessionContextPanel";
import {
  nextgenProjectPath,
  nextgenProjectsListPath,
  projectSubpathSegments,
  withProjectId,
} from "~/pages/manifold-nextgen/paths";
import { Agent, agentPath } from "~/services/agents";
import { type PublishedApp, projectAppPath } from "~/services/apps";
import {
  FeaturedSession,
  Project,
  ProjectAccessLevel,
  ProjectConversation,
  projectAgentsPath,
  projectConversationsPath,
  projectDatasetsPath,
  projectFeaturedSessionsPath,
  projectPath,
  projectRouteId,
} from "~/services/projects";
import { apiClient, getApiErrorMessage, isApiError } from "~/utils/apiClient";
import { ProjectLayoutProvider } from "./ProjectLayoutContext";
import { ProjectSidebar } from "./ProjectSidebar";
import { openAppOnEntry } from "./PublishedAppPage";
import { SessionArtifactsScope } from "./SessionArtifactsScope";
import { SessionContextSidebar } from "./SessionContextSidebar";
import { type AppSessionState, useAppSession } from "./useAppSession";

export interface ProjectOutletContext {
  projectId: string;
  project: Project;
  // Whether the caller owns (can_manage) this project. Gates owner-only controls.
  isOwner: boolean;
  sessions: ProjectConversation[] | undefined;
  featuredSessions: FeaturedSession[] | undefined;
  attachedAgents: Agent[] | undefined;
  // The roster session rows resolve their agent ids against: `attachedAgents`,
  // or an empty roster once that read has failed, so the rows show every agent
  // as unavailable instead of a loading skeleton that never resolves.
  sessionAgentRoster: Agent[] | undefined;
  attachedAgentsWithDetails: Agent[] | undefined;
  attachedDatasets: DatasetListItem[] | undefined;
  // Un-collapses the session context sidebar — for entry points outside the
  // sidebar itself (e.g. the New Session splash's Overview link) that need it
  // visible before opening a panel in it.
  expandContextSidebar: () => void;
  // Set when any fetch that gates the project contents (agents, datasets, or
  // the catalog they resolve against) fails. ProjectIndex reads this to show an
  // error instead of hanging on its loading skeleton.
  attachedEntitiesError: unknown;
  // On an app page, the caller's session with the app; it is the page's session.
  appSession: AppSessionState;
}

// One stable empty roster, so consumers memoized on it don't recompute each render.
const NO_AGENTS: Agent[] = [];

export const ProjectLayout = () => {
  // The route param is `:nextgenProjectId` so the embedded workbench subtree can
  // own `:projectId` — see the projects route in `routes.tsx`.
  const { nextgenProjectId: projectId } = useParams<{ nextgenProjectId: string }>();
  const location = useLocation();
  const currentUser = useUser();
  // A project opened/viewed — fires on mount and on switch, the one choke point
  // every entry path (home, projects list, cards, deep link) passes through.
  useEffect(() => {
    if (!projectId) return;
    trackNextgenChatEvent(NEXTGEN_CHAT_EVENT.PROJECT_OPENED, { projectId });
  }, [projectId]);
  const [projectShareOpen, setProjectShareOpen] = useState(false);
  const [preferences, updatePreferences] = useUserPreferences();
  const [isContextSidebarCollapsed, setIsContextSidebarCollapsed] = useState(
    preferences?.collapseSessionContextSidebar ?? false
  );

  const handleCollapseContextSidebar = useCallback(
    (collapsed: boolean) => {
      setIsContextSidebarCollapsed(collapsed);
      updatePreferences({ collapseSessionContextSidebar: collapsed });
    },
    [updatePreferences]
  );
  const expandContextSidebar = useCallback(
    () => handleCollapseContextSidebar(false),
    [handleCollapseContextSidebar]
  );

  const {
    collaborators,
    isLoading: collaboratorsLoading,
    collaboratorsError,
    handleShare,
    accessLevels,
    defaultAccessLevel,
  } = useProjectShare(projectId ?? "");

  const {
    data: project,
    error,
    isLoading,
  } = useFetcher<Project>(projectId ? projectPath(projectId) : null);

  const { data: attachedAgents, error: attachedAgentsError } = useFetcher<Agent[]>(
    projectId ? projectAgentsPath(projectId) : null
  );

  const agentIds = attachedAgents?.map((a) => a.id);
  // Fetches full agent details (including starter_prompts) individually because
  // GET /projects/:id/agents returns a lightweight shape without that field.
  // Ideally starter_prompts would be included in that endpoint to avoid N requests.
  const { data: fetchedAgentDetails } = useSWR<Agent[]>(
    agentIds?.length ? ["project-agents-full", ...[...agentIds].sort()] : null,
    ([, ...ids]: [string, ...string[]]) =>
      Promise.all(ids.map((id) => apiClient<Agent>(agentPath(id))))
  );
  // An empty roster has nothing to fetch, so the null key above never fills in
  // the details — and undefined details read as "still loading" downstream.
  const agentsWithDetails = attachedAgents?.length === 0 ? NO_AGENTS : fetchedAgentDetails;
  const sessionAgentRoster = attachedAgentsError ? NO_AGENTS : attachedAgents;

  const { data: attachedDatasetIds, error: attachedDatasetsError } = useFetcher<string[]>(
    projectId ? projectDatasetsPath(projectId) : null
  );

  const { data: attachedDatasets, error: catalogError } = useDatasetsById(attachedDatasetIds);

  // Any of these failing leaves attachedAgents/attachedDatasets undefined, so
  // surface the first error to ProjectIndex rather than hanging on its skeleton.
  const attachedEntitiesError = attachedAgentsError ?? attachedDatasetsError ?? catalogError;

  const { data: sessionsList } = useFetcher<{ items: ProjectConversation[] }>(
    projectId ? projectConversationsPath(projectId) : null
  );
  const sessions = sessionsList?.items;

  const { data: featuredSessions } = useFetcher<FeaturedSession[]>(
    projectId ? projectFeaturedSessionsPath(projectId) : null
  );

  // Match only the project's own routes: deeper paths (folders in a files
  // view, for example) can contain the same segments.
  const [section, sectionId, ...deeper] = projectSubpathSegments(location.pathname);
  const isLeafSection = sectionId !== undefined && deeper.length === 0;
  // An app page is a session view of the caller's own session with the app.
  const { enabled: isProjectAppsEnabled } = useProjectAppsFeature();
  const appSlug = section === "a" && isLeafSection && isProjectAppsEnabled ? sectionId : undefined;
  const appSession = useAppSession(projectId ?? "", appSlug);
  const { data: openedApp } = useFetcher<PublishedApp>(
    projectId && appSlug ? projectAppPath(projectId, appSlug) : null
  );
  const conversationId =
    section === "c" && isLeafSection ? sectionId : appSlug ? appSession.session?.id : undefined;
  // The featured-session route carries a share token instead of a
  // conversation id — the rail has no notion of the latter — so its name has
  // to be looked up in `featuredSessions` rather than `sessions`.
  const featuredToken = section === "featured" && isLeafSection ? sectionId : undefined;
  const isOwner = project?.access_level === ProjectAccessLevel.OWNER;

  // The chats route renders ProjectSetupWizard instead of the new-session
  // picker until the project has at least one enabled agent (for an owner —
  // mirrors ProjectIndex's own "setup" mode condition). Datasets are optional.
  const needsSetup = useProjectNeedsSetup(attachedAgents, isOwner);

  // The session context sidebar only shows on the new-session picker and
  // individual session pages — not on Overview, Costs, the setup wizard,
  // or the read-only featured-session view, which use the full content area
  // for their own purposes.
  const isChatsRoute = section === "c" && sectionId === undefined;
  const showContextSidebar = (isChatsRoute && !needsSetup) || !!conversationId;
  const currentSession = conversationId
    ? sessions?.find((s) => s.id === conversationId)
    : undefined;
  const currentFeaturedSession = featuredToken
    ? featuredSessions?.find((f) => f.share_token === featuredToken)
    : undefined;

  const sectionLabel = appSlug
    ? openedApp?.title
    : (currentSession?.display_name ?? currentFeaturedSession?.display_name);
  const showSectionCrumb = Boolean(conversationId || featuredToken || appSlug);

  useDocumentTitle(sectionLabel ?? project?.name ?? "");

  // Always show "All → Project Name". When inside a session (editable or a
  // featured one viewed read-only), add its name as a trailing crumb so
  // users know which conversation they're in.
  const breadcrumbs = useMemo(
    () =>
      buildEntityBreadcrumbs({
        rootHref: nextgenProjectsListPath(),
        entityName: project?.name ?? "Loading…",
        entityHref: showSectionCrumb && projectId ? nextgenProjectPath(projectId) : undefined,
        sectionLabel: showSectionCrumb ? (sectionLabel ?? "Loading…") : undefined,
      }),
    [project?.name, projectId, showSectionCrumb, sectionLabel]
  );

  const handleOpenProjectShare = useCallback(() => setProjectShareOpen(true), []);

  // Only surface header actions once the project has loaded: the modals they
  // open are rendered in the success-path return below, so controls shown
  // during load or on the error state would click into nothing.
  const headerActions = useMemo<[React.ReactNode?]>(() => {
    if (!project) {
      return [];
    }
    // Share slot represents the project (the entity the header is scoped to).
    // Owners get the full share flow; everyone else sees the same button, but
    // it opens a read-only list of owners to contact instead (below). Session
    // sharing lives inline in the composer.
    const shareNode = (
      <Button key="share-project" size="sm" className="text-sm" onClick={handleOpenProjectShare}>
        <UserRoundPlus className="size-4" />
        Share
      </Button>
    );

    return [shareNode];
  }, [project, handleOpenProjectShare]);

  useNextGenHeader({ breadcrumbs, actions: headerActions });
  usePageContext(
    project ? { page: "project", name: project.name, id: projectRouteId(project) } : null
  );

  if (!projectId) {
    return (
      <div role="alert" className="p-6 text-sm text-destructive">
        Missing project id.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full overflow-hidden">
        <div
          className="w-56 shrink-0 border-r border-border"
          role="status"
          aria-label="Loading project"
        />
        <div className="flex-1" />
      </div>
    );
  }

  if (error || !project) {
    return <ResourceLoadError error={error} resourceLabel="project" className="h-full" />;
  }

  // A project resolves from either identifier, but only its short_id addresses
  // it on the analysis-agent's create-session route, and everything below reads
  // the id straight off the URL. Home links a recent session by the project's
  // `resource_id` — its uuid — so canonicalize before rendering the outlet.
  // `short_id` is nullable until its backfill is tightened; while it is absent
  // the uuid URL is the one that works, so leave it be.
  if (project.short_id && projectId !== project.short_id) {
    return (
      <Navigate
        replace
        to={`${withProjectId(location.pathname, project.short_id)}${location.search}${location.hash}`}
      />
    );
  }

  const outletContext: ProjectOutletContext = {
    projectId,
    project,
    isOwner,
    sessions,
    featuredSessions,
    attachedAgents,
    sessionAgentRoster,
    attachedAgentsWithDetails: agentsWithDetails,
    attachedDatasets,
    attachedEntitiesError,
    expandContextSidebar,
    appSession,
  };

  return (
    <ProjectLayoutProvider value={outletContext}>
      <div className="flex h-full overflow-hidden">
        <ProjectSidebar
          projectId={projectId}
          workbenchId={project.workbench_id}
          sessions={sessions}
          isOwner={isOwner}
          featuredSessions={featuredSessions}
          sessionAgentRoster={sessionAgentRoster}
        />
        <SessionContextPanelProvider
          conversationId={conversationId}
          onExpandSidebar={expandContextSidebar}
          openAppOnEntry={openAppOnEntry(location.state)}
        >
          <McpAppsPaneProvider>
            <SessionArtifactAppsProvider>
              <SessionIngestsProvider>
                <SessionEvalRunsProvider>
                  <SessionDagRunsProvider>
                    <SessionPlanProvider conversationId={conversationId}>
                      <SessionArtifactsProvider conversationId={conversationId}>
                        <SessionArtifactsScope
                          projectUuid={project.id}
                          conversationId={conversationId}
                          canShare={isOwner}
                        >
                          <ProjectSessionArea
                            outletContext={outletContext}
                            showContextSidebar={showContextSidebar}
                            isContextSidebarCollapsed={isContextSidebarCollapsed}
                            onCollapseContextSidebar={handleCollapseContextSidebar}
                            appSession={appSlug ? appSession : undefined}
                          />
                        </SessionArtifactsScope>
                      </SessionArtifactsProvider>
                    </SessionPlanProvider>
                  </SessionDagRunsProvider>
                </SessionEvalRunsProvider>
              </SessionIngestsProvider>
            </SessionArtifactAppsProvider>
          </McpAppsPaneProvider>
        </SessionContextPanelProvider>
      </div>
      {isOwner ? (
        <ShareModal
          open={projectShareOpen}
          onOpenChange={setProjectShareOpen}
          resourceLabel="project"
          resourceName={project.name}
          collaborators={collaborators}
          collaboratorsLoading={collaboratorsLoading}
          collaboratorsError={collaboratorsError}
          accessLevels={accessLevels}
          defaultAccessLevel={defaultAccessLevel}
          onShare={handleShare}
          currentUserEmail={currentUser?.email}
          allowGeneralAccess
          renderRecipientWarning={(recipient) => (
            <ProjectShareRecipientWarning
              projectId={project.id}
              recipient={recipient}
              agents={attachedAgents}
              datasets={attachedDatasets}
            />
          )}
        />
      ) : (
        <OwnersOnlyDialog
          open={projectShareOpen}
          onOpenChange={setProjectShareOpen}
          resourceLabel="project"
          resourceName={project.name}
          ownerLevel={ProjectAccessLevel.OWNER}
          collaborators={collaborators}
          collaboratorsLoading={collaboratorsLoading}
          collaboratorsError={collaboratorsError}
        />
      )}
    </ProjectLayoutProvider>
  );
};

interface ProjectSessionAreaProps {
  outletContext: ProjectOutletContext;
  showContextSidebar: boolean;
  isContextSidebarCollapsed: boolean;
  onCollapseContextSidebar: (collapsed: boolean) => void;
  // Set on an app page, whose copilot can start a new chat.
  appSession?: AppSessionState;
}

// The routed session and its context sidebar. Lives inside the panel provider
// so a full-screen app can hand the session area to the sidebar.
const ProjectSessionArea = ({
  outletContext,
  showContextSidebar,
  isContextSidebarCollapsed,
  onCollapseContextSidebar,
  appSession,
}: ProjectSessionAreaProps) => {
  const { isAppExpanded } = useSessionContextPanel();
  const [isCopilotOpen, setIsCopilotOpen] = useState(true);
  const isCopilot = showContextSidebar && isAppExpanded;

  // The chat column is restyled, never re-parented, into the corner copilot:
  // moving <Outlet> would remount the conversation and drop its live run.
  return (
    <div className="relative flex flex-1 overflow-hidden">
      <div
        className={cn(
          "flex flex-col overflow-hidden",
          isCopilot
            ? "absolute bottom-4 right-4 z-30 h-[min(40rem,calc(100%-2rem))] w-[28rem] rounded-lg border border-border bg-background shadow-lg"
            : "relative flex-1",
          isCopilot && !isCopilotOpen && "hidden"
        )}
      >
        {isCopilot && (
          <div className="flex h-10 shrink-0 items-center gap-2 border-b border-border pl-3 pr-1">
            <Bot className="size-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Copilot</span>
            {appSession && <NewChatButton appSession={appSession} />}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsCopilotOpen(false)}
              aria-label="Minimize copilot"
              className={cn("size-8", !appSession && "ml-auto")}
            >
              <Minus className="size-4" />
            </Button>
          </div>
        )}
        <Outlet context={outletContext} />
        {showContextSidebar && isContextSidebarCollapsed && !isCopilot && (
          <Tooltip delayDuration={700}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onCollapseContextSidebar(false)}
                aria-label="Expand session context"
                className="absolute right-2 top-2 z-10"
              >
                <PanelRightOpen className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Toggle Context</TooltipContent>
          </Tooltip>
        )}
      </div>
      {isCopilot && !isCopilotOpen && (
        <Button
          onClick={() => setIsCopilotOpen(true)}
          className="absolute bottom-4 right-4 z-30 rounded-full shadow-lg"
        >
          <Bot className="size-4" />
          Ask the agent
        </Button>
      )}
      {showContextSidebar && (
        <SessionContextSidebar
          projectId={outletContext.projectId}
          projectUuid={outletContext.project.id}
          isOwner={outletContext.isOwner}
          attachedDatasets={outletContext.attachedDatasets}
          attachedAgents={outletContext.attachedAgents}
          isCollapsed={isContextSidebarCollapsed}
          onCollapse={() => onCollapseContextSidebar(true)}
          onExpand={() => onCollapseContextSidebar(false)}
        />
      )}
    </div>
  );
};

// Mirrors the help panel's clear chat: the next turn goes to a new session, and
// the current one is kept. A session no one has talked in has nothing to clear.
const NewChatButton = ({ appSession }: { appSession: AppSessionState }) => {
  const { showError } = useToast();
  const [isStarting, setStarting] = useState(false);
  const canStart = appSession.session?.listed === true && !isStarting;

  const handleNewChat = async () => {
    setStarting(true);
    try {
      await appSession.newChat();
    } catch (e) {
      showError(isApiError(e) ? getApiErrorMessage(e) : "Couldn't start a new chat.");
    } finally {
      setStarting(false);
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* Wrapped: a disabled button fires no pointer events for the tooltip. */}
        <span className="ml-auto">
          <Button
            variant="ghost"
            size="icon"
            disabled={!canStart}
            onClick={() => void handleNewChat()}
            aria-label="New chat"
            className="size-8"
          >
            <SquarePen className="size-4" />
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom">New chat</TooltipContent>
    </Tooltip>
  );
};
